import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import debounce from "lodash.debounce";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Filter } from "./types";

const BACKGROUND_COLOR = "#fff";
const BORDER_COLOR = "rgba(0,0,0,0.12)";

interface CustomTextFieldProps {
  searchedValue: string;
  setSearchedValue: (value: string) => void;
  handleSearch: (text: string, filterId: number) => void;
  filter: Filter;
}

const FilterSearch: React.FC<CustomTextFieldProps> = ({
  searchedValue,
  setSearchedValue,
  handleSearch,
  filter,
}) => {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(searchedValue);

  // Sync local state when the context value is reset externally (e.g. accordion change)
  useEffect(() => {
    setLocalValue(searchedValue);
  }, [searchedValue]);

  // Create a debounced function that updates context and triggers search
  const debouncedUpdate = useMemo(
    () =>
      debounce((text: string) => {
        setSearchedValue(text);
        handleSearch(text, filter.id);
      }, 300),
    [handleSearch, filter.id, setSearchedValue]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setLocalValue(text);
    debouncedUpdate(text);
  };

  return (
    <TextField
      hiddenLabel
      value={localValue}
      onChange={handleChange}
      placeholder={t("filters_search-placeholder")}
      size="small"
      InputProps={{
        startAdornment: (
          <SearchIcon
            style={{
              height: "24px",
              width: "24px",
              color: "rgba(0,0,0,0.56)",
            }}
          />
        ),
        endAdornment:
          localValue !== "" ? (
            <ClearIcon
              onClick={() => {
                debouncedUpdate.cancel();
                setLocalValue("");
                setSearchedValue("");
                handleSearch("", filter.id);
              }}
              style={{
                height: "24px",
                width: "24px",
                color: "rgba(0,0,0,0.56)",
                cursor: "pointer",
              }}
            />
          ) : (
            <></>
          ),
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          "&:hover fieldset": {
            borderColor: BORDER_COLOR,
          },
          "&.Mui-focused fieldset": {
            borderColor: BORDER_COLOR,
          },
        },
        width: "100%",
        border: 0,
        borderRadius: "4px",
        input: {
          paddingLeft: "8px",
          color: "rgba(0,0,0,0.87)",
          fontFamily: "Roboto",
          fontSize: "12px",
          fontWeight: "400",
          lineHeight: "20px",
          letterSpacing: "0.4px",
          backgroundColor: BACKGROUND_COLOR,
          "&:hover": {
            backgroundColor: BACKGROUND_COLOR,
          },
        },
      }}
    />
  );
};

export default FilterSearch;
